import { useEffect, useState } from 'react';
import { MIC_BLOCKED_TEXT } from '../phone/browser-help';
import { formatDuration, displayNumber } from '../phone/number';
import { MAX_PEOPLE, peopleIn, type CallView, type Phone, type PhoneState } from '../phone/phone';
import { pathLabel, qualityAdvice } from '../phone/quality';
import { Keypad } from './Keypad';
import { NumberPanel } from './NumberPanel';
import { LeaveSheet, PeopleSheet } from './PeopleSheet';
import {
  AddPersonIcon,
  HangUpIcon,
  KeypadIcon,
  MicIcon,
  MicOffIcon,
  PauseIcon,
  PeopleIcon,
  PhoneIcon,
  PlayIcon,
  SwitchIcon,
  TransferIcon,
} from './icons';
import { fullLabel, nicknameOf, type NumberNames } from '../phone/number-names';

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

function statusText(c: CallView, now: number, names?: NumberNames): string {
  switch (c.phase) {
    case 'incoming':
      // Numbers PR 2: "Incoming call to Sales · +1 (201) 285-8570" when the number has a name
      return c.to ? `Incoming call to ${nicknameOf(names, c.to) ? fullLabel(c.to, names) : displayNumber(c.to)}` : 'Incoming call';
    case 'connecting':
      return c.direction === 'inbound' ? 'Connecting…' : 'Calling…';
    case 'ringing':
      return 'Ringing…';
    case 'connected':
      return c.held ? 'On hold' : formatDuration((now - (c.connectedAt ?? now)) / 1000);
    case 'ended':
      return c.endText ?? 'Call ended';
  }
}

/** "(201) 555-0123", the caller's name, or "Conference call". */
function nameOf(c: CallView): string {
  return c.participants ? 'Conference call' : c.name || displayNumber(c.number);
}

/** The person a conference is being transferred to, while it rings / they join. */
function transferTarget(c: CallView): string | null {
  const p = c.participants?.find((x) => x.transfer);
  return p ? displayNumber(p.number) : null;
}

function peopleText(c: CallView): string {
  const n = c.participants?.filter((p) => p.state === 'joined').length ?? 0;
  const ringing = c.participants?.filter((p) => p.state === 'ringing').length ?? 0;
  return `${n} ${n === 1 ? 'person' : 'people'}${ringing ? `, ${ringing} ringing` : ''}`;
}

/**
 * The call screen. With two lines (a "Talk first" call) the line on hold is a
 * card on top; tapping it switches lines.
 */
export function CallScreen({
  phone,
  s,
  onChooseCallerId,
}: {
  phone: Phone;
  s: PhoneState;
  onChooseCallerId: (choice: string) => Promise<string | null>;
}) {
  const main = s.call!;
  const second = s.consult;
  const call = second && s.talkingOn === 'consult' ? second : main;
  const other = second ? (call === main ? second : main) : null;

  const [showKeys, setShowKeys] = useState(false);
  const [panel, setPanel] = useState<null | 'add' | 'transfer'>(null);
  const [sheet, setSheet] = useState<null | 'people' | 'leave'>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const connected = call.phase === 'connected';
  const now = useNow(connected || Boolean(other));
  const q = call.quality;
  const advice = connected && !call.held && !call.reconnecting ? qualityAdvice(q, call.relayed) : null;
  const conference = call.participants;
  const canAdd = phone.canAdd;
  const canTransfer = phone.canTransfer;

  useEffect(() => {
    if (panel === 'add' && !canAdd) setPanel(null);
    if (panel === 'transfer' && !canTransfer) setPanel(null);
  }, [panel, canAdd, canTransfer]);
  useEffect(() => {
    if (!conference) setSheet((v) => (v === 'people' ? null : v));
  }, [conference]);
  useEffect(() => setError(null), [second?.id]);

  // Typing digits during a call sends them as tones (menus, extensions).
  useEffect(() => {
    if (!connected || panel || sheet) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (/^[0-9*#]$/.test(e.key)) {
        phone.dtmf(e.key);
        setShowKeys(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [connected, panel, sheet, phone]);

  // Enter answers, Escape declines while ringing.
  useEffect(() => {
    if (call.phase !== 'incoming') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') void phone.answer();
      if (e.key === 'Escape') phone.decline();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [call.phase, phone]);

  const run = async (fn: () => Promise<string | null>) => {
    setBusy(true);
    setError(await fn());
    setBusy(false);
  };

  const hangUp = () => {
    // in a conference with two or more others, ask: leave, or end it for everyone
    const others = conference?.filter((p) => !p.you && p.state === 'joined').length ?? 0;
    if (!other && others >= 2) setSheet('leave');
    else phone.hangup();
  };

  if (panel && !other) {
    return (
      <div className="callscreen">
        <NumberPanel
          phone={phone}
          mode={panel}
          who={nameOf(main)}
          conference={Boolean(main.participants)}
          callerId={s.callerId}
          onChooseCallerId={onChooseCallerId}
          onClose={() => setPanel(null)}
        />
      </div>
    );
  }

  const switchable = other && other.phase === 'connected' && call.phase === 'connected';
  return (
    <div className={`callscreen phase-${call.phase}${call.endText?.startsWith('Transferred') ? ' ended-ok' : ''}`}>
      {other && (
        <button
          type="button"
          className={`line-card${other.phase === 'ended' ? ' line-card-ended' : ''}`}
          disabled={!switchable}
          onClick={() => phone.switchLine()}
          aria-label={switchable ? `Switch to ${nameOf(other)}` : undefined}
        >
          {other.participants ? <PeopleIcon /> : <PauseIcon />}
          <span className="line-card-text">
            <strong>{nameOf(other)}</strong>
            <span>{other.phase === 'ended' ? statusText(other, now, s.callerId?.names) : other.held ? 'On hold' : statusText(other, now, s.callerId?.names)}</span>
          </span>
          {switchable && <span className="line-card-go">Switch</span>}
        </button>
      )}

      <div className="callee">
        <div className={`ring${call.phase === 'incoming' || call.phase === 'ringing' ? ' ring-live' : ''}`} aria-hidden>
          {conference ? <PeopleIcon size={40} /> : <span>{initials(nameOf(call))}</span>}
        </div>
        <h2 className="callee-name">{nameOf(call)}</h2>
        {conference ? (
          <button type="button" className="btn btn-small btn-quiet people-link" onClick={() => setSheet('people')}>
            {peopleText(call)} · See who
          </button>
        ) : (
          call.name && <p className="callee-number">{displayNumber(call.number)}</p>
        )}
        <p className="callee-status" aria-live="polite">
          {statusText(call, now, s.callerId?.names)}
        </p>
        {call.reconnecting && connected && (
          <p className="callee-reconnect" role="status">
            Connection lost. Reconnecting audio…
          </p>
        )}
        {call.limitSoon && connected && (
          <p className="callee-limit" role="status">
            About 1 minute left: your plan minutes and credit are running out.
          </p>
        )}
        {call.dtmf && connected && <p className="callee-dtmf">{call.dtmf}</p>}
        {transferTarget(call) && call.phase === 'connected' && (
          <p className="callee-transfer" aria-live="polite">
            Transferring to {transferTarget(call)}. You’ll leave when they answer.
          </p>
        )}
      </div>

      {connected && !call.held && !call.reconnecting && (
        <div className={`quality quality-${q?.level ?? 'wait'}`} title={q ? qualityTitle(q) : undefined}>
          <span className="bars" aria-hidden>
            <i />
            <i />
            <i />
          </span>
          <span>
            {q ? `${q.level === 'good' ? 'Good' : q.level === 'fair' ? 'Fair' : 'Poor'} audio, ${pathLabel(q.path)}` : 'Checking audio…'}
          </span>
          {q && (
            <span className="quality-figures">
              {q.lossPct}% loss, {q.jitterMs ?? '–'} ms jitter, {q.rttMs ?? '–'} ms delay
            </span>
          )}
        </div>
      )}
      {advice && <p className="advice">{advice}</p>}

      {call.phase === 'incoming' && s.mic === 'denied' && (
        <p className="callee-mic" role="alert">
          {MIC_BLOCKED_TEXT} See how to fix it above.
        </p>
      )}
      {call.phase === 'incoming' ? (
        <div className="answer-row">
          <button type="button" className="round round-bad" onClick={() => phone.decline()}>
            <HangUpIcon size={26} />
            <span>Decline</span>
          </button>
          <button type="button" className="round round-ok" onClick={() => void phone.answer()} autoFocus>
            <PhoneIcon size={26} />
            <span>Answer</span>
          </button>
        </div>
      ) : (
        <>
          {showKeys && connected && <Keypad compact onKey={(k) => phone.dtmf(k)} />}
          {second && (
            <div className="xfer-steps">
              {s.consultFor === 'transfer' ? (
                <>
                  <button type="button" className="btn btn-primary" disabled={second.phase !== 'connected' || busy} onClick={() => void run(() => phone.completeTransfer())}>
                    {busy ? 'Working…' : 'Complete transfer'}
                  </button>
                  <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => phone.cancelConsult()}>
                    Cancel transfer
                  </button>
                  <p className="xfer-help">
                    {second.phase !== 'connected'
                      ? 'You can talk to them first once they answer.'
                      : main.participants
                        ? `Complete: ${nameOf(second)} joins the conference call, you leave.`
                        : `Complete: ${nameOf(main)} and ${nameOf(second)} talk, you leave.`}
                  </p>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-primary" disabled={!phone.canMerge || busy} onClick={() => void run(() => phone.merge())}>
                    {busy ? 'Merging…' : 'Merge calls'}
                  </button>
                  <p className="xfer-help">
                    {second.phase === 'connected'
                      ? 'Merge: everyone talks together. Hang up to end this call only.'
                      : second.phase === 'ended'
                        ? 'That call ended.'
                        : 'You can merge once they answer. Hang up to stop calling.'}
                  </p>
                </>
              )}
              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}
            </div>
          )}
          <div className="controls">
            <button type="button" className="ctl" aria-pressed={call.muted} disabled={!connected} onClick={() => phone.toggleMute()}>
              {call.muted ? <MicOffIcon /> : <MicIcon />}
              <span>{call.muted ? 'Unmute' : 'Mute'}</span>
            </button>
            <button type="button" className="ctl" aria-pressed={showKeys} disabled={!connected} onClick={() => setShowKeys((v) => !v)}>
              <KeypadIcon />
              <span>Keypad</span>
            </button>
            {other ? (
              <button type="button" className="ctl" disabled={!switchable} onClick={() => phone.switchLine()}>
                <SwitchIcon />
                <span>Switch</span>
              </button>
            ) : (
              <button type="button" className="ctl" aria-pressed={call.held} disabled={!connected} onClick={() => phone.toggleHold()}>
                {call.held ? <PlayIcon /> : <PauseIcon />}
                <span>{call.held ? 'Resume' : 'Hold'}</span>
              </button>
            )}
            {!other && phone.conferenceAvailable && (
              <button type="button" className="ctl" disabled={!canAdd} onClick={() => setPanel('add')} title={conference && peopleIn(call) >= MAX_PEOPLE ? `The call is full (${MAX_PEOPLE} people)` : undefined}>
                <AddPersonIcon />
                <span>{conference && peopleIn(call) >= MAX_PEOPLE ? 'Full' : 'Add'}</span>
              </button>
            )}
            {!other && s.transfer?.available && (!conference || phone.conferenceAvailable) && (
              <button
                type="button"
                className="ctl"
                disabled={!canTransfer}
                onClick={() => setPanel('transfer')}
                title={conference && peopleIn(call) >= MAX_PEOPLE ? `The call is full (${MAX_PEOPLE} people)` : undefined}
              >
                <TransferIcon />
                <span>Transfer</span>
              </button>
            )}
            {!other && conference && (
              <button type="button" className="ctl" onClick={() => setSheet('people')}>
                <PeopleIcon />
                <span>People</span>
              </button>
            )}
          </div>
          <button
            type="button"
            className="round round-bad round-hangup"
            disabled={call.phase === 'ended'}
            onClick={hangUp}
            aria-label={other ? `Hang up ${nameOf(call)}` : 'Hang up'}
          >
            <HangUpIcon size={28} />
          </button>
        </>
      )}

      {sheet === 'people' && conference && (
        <PeopleSheet
          phone={phone}
          people={conference}
          youMuted={main.muted}
          canAdd={canAdd}
          onAdd={() => {
            setSheet(null);
            setPanel('add');
          }}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === 'leave' && <LeaveSheet phone={phone} onClose={() => setSheet(null)} />}
    </div>
  );
}

function initials(s: string): string {
  const words = s.replace(/[^\p{L}\p{N} ]/gu, ' ').trim().split(/\s+/);
  if (!words[0]) return '#';
  if (/^\d/.test(words[0])) return '#';
  return (words[0][0]! + (words[1]?.[0] ?? '')).toUpperCase();
}

function qualityTitle(q: NonNullable<CallView['quality']>): string {
  return `Received ${q.inPerSec} packets/s, sent ${q.outPerSec} packets/s`;
}
